import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './stores/auth';
import { Cpu, Home } from 'lucide-react';

// Pages
import { LoginPage } from './pages/auth/Login';
import { RegisterPage } from './pages/auth/Register';
import { DashboardPage } from './pages/dashboard/Dashboard';
import { BuildsPage } from './pages/builds/Builds';
import { NewBuildPage } from './pages/builds/NewBuild';
import { BuildDetailPage } from './pages/builds/BuildDetail';
import { ApiKeysPage } from './pages/apikeys/ApiKeys';
import { StoredFilesPage } from './pages/stored-files/StoredFiles';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, loadUser } = useAuthStore();

  useEffect(() => {
    loadUser();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-mesh fixed inset-0" />
        <div className="bg-noise fixed inset-0" />
        <div className="relative z-10 text-center">
          {/* Animated logo */}
          <div className="relative mb-8">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-glow-lg animate-pulse">
              <Cpu className="w-10 h-10 text-white" />
            </div>
            <div className="absolute inset-0 w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 blur-xl opacity-50 animate-pulse" />
          </div>
          
          {/* Loading spinner */}
          <div className="relative w-12 h-12 mx-auto">
            <div className="absolute inset-0 rounded-full border-2 border-slate-800" />
            <div className="absolute inset-0 rounded-full border-2 border-cyan-500 border-t-transparent animate-spin" />
          </div>
          
          <p className="mt-6 text-slate-500 font-mono text-sm animate-pulse">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="bg-mesh fixed inset-0" />
      <div className="bg-noise fixed inset-0" />
      <div className="fixed inset-0 bg-grid opacity-20" />
      
      <div className="relative z-10 text-center px-4">
        {/* 404 text with gradient */}
        <h1 className="text-[150px] md:text-[200px] font-bold leading-none mb-4">
          <span className="text-gradient">404</span>
        </h1>
        
        <p className="text-2xl text-white mb-2">Page not found</p>
        <p className="text-slate-500 mb-8 max-w-md mx-auto">
          The page you're looking for doesn't exist or has been moved.
        </p>
        
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white font-semibold hover:shadow-glow transition-all duration-300"
        >
          <Home className="w-4 h-4" />
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/builds"
            element={
              <ProtectedRoute>
                <BuildsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/builds/new"
            element={
              <ProtectedRoute>
                <NewBuildPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/builds/:id"
            element={
              <ProtectedRoute>
                <BuildDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Placeholder routes */}
          <Route
            path="/configs"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/credits"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/api-keys"
            element={
              <ProtectedRoute>
                <ApiKeysPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stored-files"
            element={
              <ProtectedRoute>
                <StoredFilesPage />
              </ProtectedRoute>
            }
          />

          {/* Redirect root to dashboard or login */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
